import React, { useEffect, useRef, useMemo } from "react";

export default function BillsChart({ bills }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const last12 = useMemo(() => {
    return [...bills]
      .sort((a, b) => (a.bill_date > b.bill_date ? 1 : -1))
      .slice(-12);
  }, [bills]);

  const labels = last12.map((b) => {
    const [year, month] = b.bill_date.split("-");
    return new Date(Number(year), Number(month) - 1).toLocaleString("default", {
      month: "short",
      year: "2-digit",
    });
  });

  const tenantData = last12.map((b) => b.totals?.tenant_total ?? 0);
  const landlordData = last12.map((b) => b.totals?.landlord_total ?? 0);
  const totals = last12.map((b) => b.totals?.grand_total ?? 0);

  const avgTenant = tenantData.length
    ? tenantData.reduce((s, v) => s + v, 0) / tenantData.length
    : 0;
  const avgLandlord = landlordData.length
    ? landlordData.reduce((s, v) => s + v, 0) / landlordData.length
    : 0;
  const maxTotal = totals.length ? Math.max(...totals) : 0;
  const minTotal = totals.length ? Math.min(...totals) : 0;
  const maxIdx = totals.indexOf(maxTotal);
  const minIdx = totals.indexOf(minTotal);

  const fmt = (ym) => {
    if (!ym) return "—";
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1).toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (!canvasRef.current || last12.length === 0) return;
    if (chartRef.current) chartRef.current.destroy();
    const Chart = window.Chart;
    if (!Chart) return;

    // Use current theme to style grid and labels
    const isDark = document.documentElement.classList.contains("dark");
    const gridColor = isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)";
    const textColor = isDark ? "#8898b8" : "#475569";
    const tooltipBg = isDark ? "rgba(18,25,41,0.95)" : "rgba(255,255,255,0.95)";
    const tooltipBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)";
    const tooltipText = isDark ? "#e8e4ff" : "#0f172a";

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Tenant Share",
            data: tenantData,
            backgroundColor: "rgba(2, 132, 199, 0.8)",
            hoverBackgroundColor: "#0284c7",
            borderRadius: 0,
            borderSkipped: false,
          },
          {
            label: "Landlord Share",
            data: landlordData,
            backgroundColor: "rgba(124, 58, 237, 0.8)",
            hoverBackgroundColor: "#7c3aed",
            borderRadius: {
              topLeft: 6,
              topRight: 6,
              bottomLeft: 0,
              bottomRight: 0,
            },
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: tooltipBg,
            borderColor: tooltipBorder,
            borderWidth: 1,
            titleColor: textColor,
            bodyColor: tooltipText,
            padding: 12,
            cornerRadius: 12,
            shadowColor: "rgba(0,0,0,0.15)",
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: $${ctx.parsed.y.toFixed(2)}`,
              afterBody: (items) => [
                `Total: $${items.reduce((s, i) => s + i.parsed.y, 0).toFixed(2)}`,
              ],
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              color: textColor,
              font: { size: 10.5, family: '"Instrument Sans", sans-serif', weight: "bold" },
              autoSkip: false,
              maxRotation: 0,
            },
            border: { display: false },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: gridColor },
            border: { display: false },
            ticks: {
              color: textColor,
              font: { size: 10.5, family: '"DM Mono", monospace', weight: "500" },
              callback: (v) => "$" + v,
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [last12, tenantData, landlordData, labels]);

  if (last12.length === 0) {
    return (
      <div className="text-center py-12 text-text-muted text-[14px]">No billing history yet.</div>
    );
  }

  const cards = [
    { label: "Avg tenant / mo", value: `$${avgTenant.toFixed(2)}`, sub: `last ${last12.length} bills` },
    { label: "Avg landlord / mo", value: `$${avgLandlord.toFixed(2)}`, sub: `last ${last12.length} bills` },
    { label: "Highest total", value: `$${maxTotal.toFixed(2)}`, sub: fmt(last12[maxIdx]?.bill_date) },
    { label: "Lowest total", value: `$${minTotal.toFixed(2)}`, sub: fmt(last12[minIdx]?.bill_date) },
  ];

  return (
    <div className="glass-panel p-6 space-y-6">
      {/* Dynamic Summary Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(({ label, value, sub }) => (
          <div
            key={label}
            className="bg-surface-2 border border-border rounded-2xl px-4 py-3.5 transition-all hover:bg-surface-3 hover:-translate-y-0.5 duration-200"
          >
            <div className="text-[10px] text-text-muted uppercase tracking-[0.08em] font-extrabold mb-1">
              {label}
            </div>
            <div className="font-mono text-[18px] font-extrabold text-text-primary leading-tight">
              {value}
            </div>
            <div className="text-[11.5px] text-text-muted mt-1.5 font-medium">{sub}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-4 text-[12px] text-text-muted items-center font-semibold">
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-sky-600 shrink-0 shadow-sm shadow-sky-600/20" />
          Tenant Share
        </span>
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-violet shrink-0 shadow-sm shadow-violet/20" />
          Landlord Share
        </span>
      </div>

      <div className="relative w-full h-[280px]">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`Stacked bar chart showing tenant and landlord shares for the last ${last12.length} bills`}
        />
      </div>
    </div>
  );
}
